import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getPlatformSettings } from "../services/settings";
import { useAuth } from "./AuthContext";

const PlatformSettingsContext = createContext(null);

const DEFAULTS = {
  maintenanceMode: { enabled: false },
  featureFlags: { staking: true, offerwall: true, leaderboard: true, referrals: true, cptToCisConversion: false },
  referralRewards: {
    signupReferrerCis: 100,
    signupReferredCis: 100,
    completionReferrerCis: 1900,
    completionReferredCis: 900,
    stakingReferrerCis: 3000,
  },
  cisRewards: {
    post: 10,
    likeReceived: 0.1,
    commentReceived: 0.5,
    streakLoginDay: 40,
    stakingFactor: 0.2,
    cptToCisRate: 40,
  },
};

export function PlatformSettingsProvider({ children }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    // Settings require auth on the backend, so only fetch once a user
    // session exists; otherwise keep defaults (everything enabled) so
    // logged-out screens like /login never block on this.
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getPlatformSettings()
      .then(setSettings)
      .catch(() => {}) // keep last-known-good (or defaults) on failure
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    refresh();
    // Re-check periodically so an admin toggle takes effect for already-open
    // tabs without requiring a manual refresh.
    const interval = setInterval(refresh, 60_000);
    return () => clearInterval(interval);
  }, [refresh]);

  const isFeatureEnabled = useCallback(
    (key) => settings.featureFlags?.[key] !== false,
    [settings.featureFlags]
  );

  return (
    <PlatformSettingsContext.Provider
      value={{
        maintenanceMode: settings.maintenanceMode,
        featureFlags: settings.featureFlags,
        referralRewards: settings.referralRewards,
        cisRewards: settings.cisRewards,
        isFeatureEnabled,
        loading,
        refresh,
      }}
    >
      {children}
    </PlatformSettingsContext.Provider>
  );
}

export function usePlatformSettings() {
  const ctx = useContext(PlatformSettingsContext);
  if (!ctx) throw new Error("usePlatformSettings must be used within a PlatformSettingsProvider");
  return ctx;
}
